/**
 * The error codes the moderation surface can answer with (U3).
 *
 * Declared here up front rather than as api-local literals a later phase
 * promotes — `MEDIA_ERROR_CODES` and `VERIFICATION_ERROR_CODES` both had to be
 * lifted after the fact, and `CHAT_ERROR_CODES` (P18a) proved doing it early
 * costs nothing.
 *
 * **The list is short because most of this surface does not refuse.** Blocking
 * somebody already blocked, unblocking somebody who was never blocked, and
 * reporting somebody with a report already open are all *the goal already
 * achieved* — they answer success, the way `PhotoDeleteModal` treats a 404 on a
 * delete. A member trying to get away from someone must never meet an error
 * message for pressing the button twice.
 */
export const MODERATION_ERROR_CODES = {
  /**
   * The member aimed this at themselves.
   *
   * 422, and **one** code for both blocking and reporting: no surface offers
   * either action on your own profile, so reaching it at all is a client bug,
   * and it should be visible in logs as one thing rather than two.
   */
  selfActionForbidden: 'moderation_self_action_forbidden',
  /**
   * There is no such member.
   *
   * 404, and deliberately one code for the block and the report paths — the
   * client does the same thing either way, which is to go back.
   *
   * Note what this does **not** check: whether the target is visible to the
   * caller. A member must be able to block somebody who has hidden themselves,
   * or who has already blocked them — a block that only works on people you can
   * still see is a block that stops working exactly when it matters.
   */
  targetNotFound: 'moderation_target_not_found',
  /**
   * The report names a conversation the reporter is not in.
   *
   * 422. The ref decides which chat a reviewer opens, so an unchecked one would
   * let any member point staff at a third party's conversation. Refused rather
   * than quietly dropped: no client builds this by hand, so it is a bug worth
   * seeing rather than a stray field worth ignoring.
   */
  reportContextRefUnusable: 'moderation_report_context_ref_unusable',
  /**
   * No such report — a **staff** code.
   *
   * 404. Reached from the review queue only, so unlike the member codes above it
   * says nothing to anybody who is not already permitted to read reports.
   */
  reportNotFound: 'moderation_report_not_found',
  /**
   * Somebody else resolved this report first.
   *
   * 409, and it is the refusal branch of the resolution's **claim** (N27): the
   * write re-checks `status = 'pending_review'` in its own `WHERE`, so the first
   * reviewer's verdict stands and the second is told rather than silently
   * overwriting it. Two staff working the same live queue is the normal case,
   * not the exception — the queue polls (W5), so both were looking at the same
   * row seconds ago.
   */
  reportAlreadyResolved: 'moderation_report_already_resolved',

  /*
   * ---- Violations and enforcement (P20a) ----
   *
   * Added to this file rather than a second one, deliberately: the api's
   * exception filter maps one code union per domain, and a `MODERATION_*` split
   * across two files is how a code ends up mapped in one place and unmapped in
   * the other.
   */

  /**
   * No such violation type — a **staff** code.
   *
   * 404. Reached from the catalogue only, which sits behind `violations.manage`.
   */
  violationTypeNotFound: 'moderation_violation_type_not_found',
  /**
   * A type with this key already exists.
   *
   * 409. The key is the classifier label and the join every historical case was
   * judged under, so it is unique and it is never edited — a collision is a real
   * refusal rather than something to disambiguate silently. Note that a
   * **deactivated** type still holds its key: that is the point of deactivating
   * rather than deleting, and re-using the key of a retired category would
   * re-point old cases at a new meaning.
   */
  violationTypeKeyTaken: 'moderation_violation_type_key_taken',
  /**
   * No such violation.
   *
   * 404, and it serves **both** the staff queue and the member's own notice
   * route — a member asking about somebody else's violation gets the same answer
   * as one asking about a violation that never existed, which is the only answer
   * that does not confirm it exists.
   */
  violationNotFound: 'moderation_violation_not_found',
  /**
   * This violation is not in a state where that decision can be made.
   *
   * 409, and it is the refusal branch of the enforcement **claim** (N27): every
   * write re-checks the status it was decided against in its own `WHERE`, so a
   * second moderator working the same live queue is told rather than silently
   * overwriting the first verdict. Two staff on one polling queue (W5) is the
   * normal case, not the exception.
   */
  violationNotInReviewableState: 'moderation_violation_not_reviewable',
  /**
   * The member has already appealed this one.
   *
   * 409. One appeal per enforcement — the state machine has no edge back into
   * `appealed`. Unlike a repeat *report*, which upserts so a frightened member
   * never meets an error, overwriting an appeal would discard the submission a
   * moderator may already be reading.
   */
  violationAlreadyAppealed: 'moderation_violation_already_appealed',
  /**
   * There is nothing here to appeal.
   *
   * 422. Only an `enforced` violation has had a consequence
   * (`isAppealableViolationStatus`); a queued detection has not affected the
   * member yet, and a dismissal already went their way.
   */
  violationNotAppealable: 'moderation_violation_not_appealable',
  /**
   * The violation type's configured action cannot run against this subject.
   *
   * 422, and today it means exactly one thing: a type configured to
   * `hide_message` was reached by a **behavioural** violation, which has no
   * message to hide (P27a1). It is an operator misconfiguration, not a member
   * or moderator mistake — so it is refused **before** the enforcement claim is
   * taken, leaving the case in the queue for somebody to decide once the type is
   * fixed. Recording an action that could not run would be worse: the record
   * would say a consequence happened, and none did.
   */
  violationActionUnavailable: 'moderation_violation_action_unavailable',
  /**
   * A behavioural violation type cannot have `reviewBeforeEnforce` switched off.
   *
   * 422. The behavioural detector queues every flag for a human regardless of
   * the switch (P27a2): it counts, it does not judge, and a count is not a
   * verdict anybody should be suspended on unread. An "off" that nothing reads
   * would be a lie in the catalogue — the list would say "auto-enforce" and the
   * engine would keep queueing — so the api refuses it, and the admin's editor
   * locks the switch with the same reason (P27a3).
   */
  violationTypeReviewLocked: 'moderation_violation_type_review_locked',
} as const;

export type ModerationErrorCode =
  (typeof MODERATION_ERROR_CODES)[keyof typeof MODERATION_ERROR_CODES];
